import { useCallback, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { ConfirmAlert } from "../../Alerts/Alerts";
import { useTranslationApp } from "../../../lib/i18next";
import GalleryLabeledActionButton from "../../Gallery/GalleryLabeledActionButton";
import ModalMedia from "../ModalMedia";
import {
  COMPETITOR_SCOPE_MODAL_TITLE,
  BUTTON_GENERATE,
  MODAL_COMPETITORS_CONFIRM_REFERENCE_DESCRIPTION,
  MODAL_COMPETITORS_CONFIRM_REFERENCE_TITLE,
  MODAL_COMPETITORS_PREPARING_REFERENCE,
  MODAL_COMPETITORS_SAVING_AD,
  MODAL_COMPETITORS_FAVORITES_EMPTY,
  MODAL_COMPETITORS_ADD_OPTION,
  MODAL_CREATE_WITH_IA_META_AD_LIBRARY_LOADING,
  MODAL_GALLERY_META_AD_LIBRARY_EMPTY_CONTENT,
} from "../../../i18n/keysTranslations";
import { getReferenceAdStoragePath } from "../../../utils/constants";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { useDispatchApp } from "../../../lib/redux";
import { useRebrandBanner } from "../../../hooks/useRebrandBanner";
import { useCompetitorAdFavorites } from "../../../hooks/useCompetitorAdFavorites";
import {
  formatCompetitorFavoritesForGallery,
  filterGalleryItemsByCompetitor,
  filterGalleryItemsByPlatform,
  hasCompetitorAdFavorites,
  isMetaAdLibraryViewOnlyItem,
} from "../../../services/metaAdLibrary";

import {
  COMPETITOR_FILTER_SELECT_SX,
  MODAL_GALLERY_FETCH_MODE_META_AD_LIBRARY,
} from "../ModalGalleryFromUrl/constants";
import { useGalleryFromUrlSelection } from "../ModalGalleryFromUrl/useGalleryFromUrlSelection";
import { useGalleryFromMetaAdLibrary } from "../ModalGalleryFromUrl/useGalleryFromMetaAdLibrary";
import ModalGalleryFromUrlLayout from "../ModalGalleryFromUrl/ModalGalleryFromUrlLayout";
import CompetitorFilterActions from "./CompetitorFilterActions";
import ModalCompetitorsEdit from "./ModalCompetitorsEdit";
import {
  MODAL_COMPETITORS_FROM_CREATE_WITH_IA,
  MODAL_COMPETITORS_FROM_MENU,
  COMPETITOR_FILTER_ADD_VALUE,
} from "./constants";

const AddCompetitorOptionLabel = ({ label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <AddIcon
      sx={{
        ml: -0.5,
        fontSize: 20,
        color: "background.paperContrastText",
      }}
    />
    <Typography variant="body2" component="span">
      {label}
    </Typography>
  </Box>
);

const ModalCompetitors = ({
  modalOpen,
  onCloseModal,
  onCallbackSelectedItems,
  from = MODAL_COMPETITORS_FROM_MENU,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const pathStorageReferenceAd = getReferenceAdStoragePath(userID);

  const isMenuMode = from === MODAL_COMPETITORS_FROM_MENU;
  const isCreateWithIAMode = from === MODAL_COMPETITORS_FROM_CREATE_WITH_IA;

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [businessCompetitors, setBusinessCompetitors] = useState([]);
  const [selectPreview, setSelectPreview] = useState(null);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showEditCompetitors, setShowEditCompetitors] = useState(false);

  const { totalTopOffsetPx } = useRebrandBanner();

  const {
    favoritesByPageId,
    isFavorite,
    isTogglingFavorite,
    onToggleFavorite,
  } = useCompetitorAdFavorites({
    modalOpen,
    dispatch,
  });

  const metaAdLibrary = useGalleryFromMetaAdLibrary({
    dispatch,
    pathStorageReferenceAd,
    businessCompetitors,
    setData,
    setError,
    setIsLoading,
    setIsUploading,
    setBusinessCompetitors,
    data,
  });

  const refreshCompetitorsAds = metaAdLibrary.onGetInitialData;

  const galleryDisplayData = useMemo(() => {
    const baseItems = showFavoritesOnly
      ? formatCompetitorFavoritesForGallery({ favoritesByPageId })
      : metaAdLibrary.galleryDisplayData;
    const platformItems = showFavoritesOnly
      ? filterGalleryItemsByPlatform({
          items: baseItems,
          platformFilterValue: metaAdLibrary.selectedPlatform,
        })
      : baseItems;

    return filterGalleryItemsByCompetitor({
      items: platformItems,
      competitorFilterValue: metaAdLibrary.selectedCompetitorFilter,
    });
  }, [
    favoritesByPageId,
    metaAdLibrary.galleryDisplayData,
    metaAdLibrary.selectedCompetitorFilter,
    metaAdLibrary.selectedPlatform,
    showFavoritesOnly,
  ]);

  const hasFavorites = useMemo(
    () => hasCompetitorAdFavorites(favoritesByPageId),
    [favoritesByPageId]
  );

  const {
    resetSelection,
    onSelectItem: onPickerSelectItem,
    selectedGalleryIndexes,
    getSelectedItems,
    setSelectedPickerIndex,
    setSelectedItems,
  } = useGalleryFromUrlSelection({
    galleryDisplayData,
    multiple: false,
  });

  const onCompetitorsSaved = useCallback(
    async ({ savedCompetitors }) => {
      setBusinessCompetitors(savedCompetitors);
      setShowEditCompetitors(false);
      setShowFavoritesOnly(false);
      resetSelection();
      setSelectPreview(null);
      await refreshCompetitorsAds({ refreshAdsOnly: true });
    },
    [refreshCompetitorsAds, resetSelection]
  );

  const onCloseEditCompetitors = useCallback(() => {
    setShowEditCompetitors(false);
  }, []);

  const onGetInitialData = useCallback(async () => {
    resetSelection();
    setSelectPreview(null);
    await metaAdLibrary.onGetInitialData();
    //eslint-disable-next-line
  }, [metaAdLibrary.onGetInitialData, resetSelection]);

  const onSelectPreview = useCallback((indexOrData) => {
    setSelectPreview(indexOrData);
  }, []);

  const previewItem = useMemo(() => {
    if (selectPreview === null) return null;

    const previewIndex =
      typeof selectPreview === "object" && selectPreview !== null
        ? selectPreview.index
        : selectPreview;

    return galleryDisplayData[previewIndex] ?? null;
  }, [galleryDisplayData, selectPreview]);

  useEffect(() => {
    if (!modalOpen) {
      setSelectPreview(null);
      setShowFavoritesOnly(false);
      setShowEditCompetitors(false);
    }
  }, [modalOpen]);

  useEffect(() => {
    resetSelection();
    setSelectPreview(null);
    setShowFavoritesOnly(false);
  }, [
    metaAdLibrary.selectedCompetitorFilter,
    metaAdLibrary.selectedPlatform,
    resetSelection,
  ]);

  const processSelectedItems = useCallback(
    async (itemsToProcess) => {
      if (!itemsToProcess.length || isUploading) return;

      await metaAdLibrary.onSendSelectedItems(itemsToProcess, {
        onCallbackSelectedItems,
        onCloseModal,
      });
    },
    [isUploading, metaAdLibrary, onCallbackSelectedItems, onCloseModal]
  );

  const onGenerateCompetitorItem = useCallback(
    async ({ item, index = null }) => {
      if (!item?.id || isMetaAdLibraryViewOnlyItem(item) || isUploading) return;

      if (isCreateWithIAMode) {
        const response = await ConfirmAlert({
          title: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_TITLE),
          text: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_DESCRIPTION),
          showCancelButton: true,
        });
        if (!response.isConfirmed) return;
      }

      if (index !== null) setUploadingIndex(index);
      try {
        await processSelectedItems([item]);
      } finally {
        if (index !== null) setUploadingIndex(null);
        if (isCreateWithIAMode) {
          setSelectedPickerIndex(null);
          setSelectedItems([]);
        }
      }
    },
    [
      isCreateWithIAMode,
      isUploading,
      processSelectedItems,
      setSelectedItems,
      setSelectedPickerIndex,
      t,
    ]
  );

  const onDirectAddItem = useCallback(
    async ({ index }) => {
      const items = getSelectedItems([index]);
      if (!items.length) return;

      await onGenerateCompetitorItem({ item: items[0], index });
    },
    [getSelectedItems, onGenerateCompetitorItem]
  );

  const onSelectItemWithConfirm = useCallback(
    async (index) => {
      const item = galleryDisplayData[index];
      if (!item?.id || isMetaAdLibraryViewOnlyItem(item) || isUploading) {
        return;
      }

      const wasSelected = selectedGalleryIndexes.includes(index);
      onPickerSelectItem(index);

      if (wasSelected) {
        return;
      }

      const response = await ConfirmAlert({
        title: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_TITLE),
        text: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_DESCRIPTION),
        showCancelButton: true,
      });

      if (!response.isConfirmed) {
        onPickerSelectItem(index);
        return;
      }

      setUploadingIndex(index);
      try {
        await processSelectedItems([item]);
      } finally {
        setUploadingIndex(null);
        setSelectedPickerIndex(null);
        setSelectedItems([]);
      }
    },
    [
      galleryDisplayData,
      isUploading,
      onPickerSelectItem,
      processSelectedItems,
      selectedGalleryIndexes,
      setSelectedItems,
      setSelectedPickerIndex,
      t,
    ]
  );

  const onSelectItem = isCreateWithIAMode
    ? onSelectItemWithConfirm
    : onPickerSelectItem;

  useEffect(() => {
    if (!modalOpen) return;
    onGetInitialData();
  }, [modalOpen, onGetInitialData]);

  const previewGenerateAction = useMemo(() => {
    if (!previewItem || isMetaAdLibraryViewOnlyItem(previewItem)) {
      return null;
    }

    return (
      <GalleryLabeledActionButton
        variant="secondary"
        className="gallery-direct-add-button"
        label={t(BUTTON_GENERATE)}
        icon={AddIcon}
        onClick={() => onGenerateCompetitorItem({ item: previewItem })}
        loading={isUploading}
        disabled={isUploading}
      />
    );
  }, [previewItem, isUploading, onGenerateCompetitorItem, t]);

  const getIsCompetitorFavorite = useCallback(
    (item) =>
      isFavorite({
        pageId: item?.pageId,
        libraryID: item?.libraryID ?? item?.id,
        item,
      }),
    [isFavorite]
  );

  const getIsCompetitorFavoriteLoading = useCallback(
    (item) =>
      isTogglingFavorite({
        pageId: item?.pageId,
        libraryID: item?.libraryID ?? item?.id,
      }),
    [isTogglingFavorite]
  );

  const onToggleCompetitorFavoriteItem = useCallback(
    ({ item }) => onToggleFavorite({ item }),
    [onToggleFavorite]
  );

  const onToggleShowFavoritesOnly = useCallback(() => {
    setShowFavoritesOnly((current) => !current);
    resetSelection();
    setSelectPreview(null);
  }, [resetSelection]);

  const onOpenEditCompetitors = useCallback(() => {
    setShowEditCompetitors(true);
    resetSelection();
    setSelectPreview(null);
  }, [resetSelection]);

  const competitorFilterOptionsWithAdd = useMemo(() => {
    const addOption = {
      value: COMPETITOR_FILTER_ADD_VALUE,
      label: (
        <AddCompetitorOptionLabel label={t(MODAL_COMPETITORS_ADD_OPTION)} />
      ),
    };

    return [addOption, ...metaAdLibrary.competitorFilterOptions];
  }, [metaAdLibrary.competitorFilterOptions, t]);

  const onCompetitorFilterValidationChange = useCallback(
    async (value) => {
      if (value === COMPETITOR_FILTER_ADD_VALUE) {
        onOpenEditCompetitors();
        return false;
      }

      return true;
    },
    [onOpenEditCompetitors]
  );

  const competitorFilterAccessory = useMemo(
    () => (
      <CompetitorFilterActions
        t={t}
        showFavoritesOnly={showFavoritesOnly}
        onToggleShowFavoritesOnly={onToggleShowFavoritesOnly}
      />
    ),
    [onToggleShowFavoritesOnly, showFavoritesOnly, t]
  );

  const emptyContentKeyOverride =
    showFavoritesOnly && galleryDisplayData.length === 0
      ? MODAL_COMPETITORS_FAVORITES_EMPTY
      : null;

  return (
    <>
      <ModalGalleryFromUrlLayout
        t={t}
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        titleKey={COMPETITOR_SCOPE_MODAL_TITLE}
        isLoading={isLoading}
        loadingMessageKey={MODAL_CREATE_WITH_IA_META_AD_LIBRARY_LOADING}
        error={error}
        totalTopOffsetPx={totalTopOffsetPx}
        emptyContentKey={MODAL_GALLERY_META_AD_LIBRARY_EMPTY_CONTENT}
        emptyContentKeyOverride={emptyContentKeyOverride}
        isMetaAdLibraryMode={true}
        showCompetitorFilter={
          !isLoading &&
          !error &&
          (data.length > 0 || hasFavorites || businessCompetitors.length > 0)
        }
        competitorFilterControl={metaAdLibrary.control}
        competitorFilterOptions={competitorFilterOptionsWithAdd}
        platformFilterOptions={metaAdLibrary.platformFilterOptions}
        onPlatformFilterChange={metaAdLibrary.onPlatformFilterChange}
        competitorFilterAccessory={competitorFilterAccessory}
        competitorFilterSelectSx={COMPETITOR_FILTER_SELECT_SX}
        competitorFilterValidationChange={onCompetitorFilterValidationChange}
        galleryDisplayData={galleryDisplayData}
        hasSelection={false}
        isUploading={isUploading}
        onConfirmSelection={() => {}}
        showUploadingFooter={isCreateWithIAMode}
        uploadingFooterLabels={[
          MODAL_COMPETITORS_SAVING_AD,
          MODAL_COMPETITORS_PREPARING_REFERENCE,
        ]}
        galleryProps={{
          data: galleryDisplayData,
          onCloseModal,
          lastItemRef: undefined,
          loadLastItem: true,
          onFailedLoadUrl: () => {},
          onSelectItem,
          selectedItems: selectedGalleryIndexes,
          onSelectPreview,
          multiple: isMenuMode ? false : true,
          overlayPickerMode: isCreateWithIAMode,
          selectedItemsArray: isCreateWithIAMode ? selectedGalleryIndexes : [],
          showActionButtons: true,
          showPreviewExpandOnly: true,
          showMetaAdLibraryLinkButton: true,
          showCompetitorFavoriteButton: true,
          getIsCompetitorFavorite,
          getIsCompetitorFavoriteLoading,
          onToggleCompetitorFavoriteItem,
          showEditButton: false,
          showDeleteButton: false,
          showPostButton: false,
          showDirectAddButton: isMenuMode,
          directAddLabel: t(BUTTON_GENERATE),
          onDirectAddItem,
          isDirectAddLoading: isUploading,
          directAddLoadingIndex: uploadingIndex,
          showSourceSelector: false,
          showFiltersSelector: false,
          showRating: false,
          showMetaAdDuration: true,
          from: MODAL_GALLERY_FETCH_MODE_META_AD_LIBRARY,
          acceptedFormats: [],
          minDimensions: {},
        }}
      />

      <ModalCompetitorsEdit
        modalOpen={showEditCompetitors}
        onCloseModal={onCloseEditCompetitors}
        businessCompetitors={businessCompetitors}
        dispatch={dispatch}
        onSaved={onCompetitorsSaved}
      />

      {previewItem ? (
        <ModalMedia
          modalOpen={selectPreview !== null}
          onCloseModal={() => setSelectPreview(null)}
          url={previewItem.url}
          item={previewItem}
          showEditButton={false}
          showShareButton={false}
          showDeleteButton={false}
          showDownloadButton={false}
          customActions={previewGenerateAction}
        />
      ) : null}
    </>
  );
};

export default ModalCompetitors;
